import {
  Audio,
  AudioFormValidationProperties,
  AudioStatusReasonType,
  AudioStatusType,
  AudioTypesDDL,
} from 'types/audio';
import {
  _SongsLinks,
  Song,
  SongDDL,
  SongPatchValidationSchema,
  SongListQueryParams,
  BaseSongListQueryParams,
  Folder,
  FolderItem,
} from 'types/song';
import {
  ContributionClassesAndRolesDDL,
  ContributionClassRolesDDL,
  ContributionValidationSchema,
} from 'types/contribution';
import { LatestSplit, Member } from 'types/splits.types';
import { LyricValidationSchema } from 'types/lyrics.types';
import { LoadingState } from 'types/enums';
import { DDL } from 'types/general.types';
import { GenericObject } from 'types/generic.types';

export interface SongListState {
  songs: Song[];
  count?: number;
  _links?: _SongsLinks;
  song?: Song;
  validations?: SongPatchValidationSchema;
  lyricValidation?: LyricValidationSchema;
  createContributionValidation?: ContributionValidationSchema;
  createSongLoadingState: LoadingState;
  fromId?: string;
  languageCodes?: DDL;
  songDDL?: SongDDL;
  localAudio: {
    [songGuid: string]: Audio[];
  };
  audioListReadyForUpload: string[];
  contributionsLoadingState: LoadingState;
  updatingContributions: boolean;
  songAudioTypes?: AudioTypesDDL;
  songAudioValidations?: AudioFormValidationProperties;
  contributionClassesAndRolesDDL?: ContributionClassesAndRolesDDL;
  contributionClassesDDL?: ContributionClassRolesDDL;
  possibleSplitMembers?: Member[];
  latestSplit?: LatestSplit;
  isSettingPrivateAudio: GenericObject<boolean>;
  isFetchingAudio: GenericObject<boolean>;
  isFetchingSongs: boolean;
  searchKeyword?: string;
  collaboratorSearchKeyword?: string;
  songListValidations?: SongListQueryParams;
  songAudioListValidations?: BaseSongListQueryParams;
  folder?: Folder;
  pinned?: {
    songs: Song[];
  };
}

export const LOCAL_AUDIO_GUID_PREFIX = 'local-audio-guid-';

const generateLocalAudioGuid = (audioList: Audio[] = []) => {
  const guidNumber = audioList?.reduce((acc, audio) => {
    if (audio.guid.includes(LOCAL_AUDIO_GUID_PREFIX)) {
      const extractedAudioGuidNumber = Number(
        audio.guid.replace(LOCAL_AUDIO_GUID_PREFIX, '')
      );
      return extractedAudioGuidNumber >= acc
        ? extractedAudioGuidNumber + 1
        : acc;
    }
    return acc;
  }, 0);
  return `${LOCAL_AUDIO_GUID_PREFIX}${guidNumber}`;
};

export const isLocalAudioGuid = (guid: string) =>
  guid.includes(LOCAL_AUDIO_GUID_PREFIX);

const getLocalAudioList = (state: SongListState) => {
  if (!state.song) return [];
  return state.localAudio[state.song.guid] || [];
};

interface UpdateStateWithAudioOptions {
  addAudio: boolean;
  createLocalAudio: boolean;
  replaceAudio?: Audio;
  additionalSongState?: Partial<Song>;
}

export const getUpdatedAudioListState = (
  updatedAudio: Audio,
  audioGuid: string,
  exists: boolean,
  audioListState?: Audio[],
  addAudio = true
) => {
  if (audioListState) {
    if (exists) {
      return audioListState.map((audio) =>
        audio.guid === audioGuid ? { ...audio, ...updatedAudio } : audio
      );
    }
    if (addAudio) {
      return [updatedAudio, ...audioListState];
    }
    return audioListState;
  }
  if (addAudio) {
    return [updatedAudio];
  }
  return [];
};

export const updateStateWithAudio = (
  state: SongListState,
  audio: Audio,
  options: Partial<UpdateStateWithAudioOptions> = {}
) => {
  const { addAudio = true, createLocalAudio = true } = options;
  const audioGuidToUpdate = options?.replaceAudio?.guid || audio.guid;
  const currentAudioExists = Boolean(
    state.song?.audio?.find((audio) => audio.guid === audioGuidToUpdate)
  );
  const localAudioExists = Boolean(
    state.localAudio[audio.parent]?.find(
      (audio) => audio.guid === audioGuidToUpdate
    )
  );
  if (state.song) {
    state.song.audio = getUpdatedAudioListState(
      audio,
      audioGuidToUpdate,
      currentAudioExists,
      state.song.audio,
      addAudio
    );
  }
  state.localAudio[audio.parent] = getUpdatedAudioListState(
    audio,
    audioGuidToUpdate,
    localAudioExists,
    state.localAudio[audio.parent],
    Boolean(addAudio && createLocalAudio)
  );
};

export const addOrReplaceAudio = (
  state: SongListState,
  audio: Audio,
  replaceAudio?: Audio
) => {
  updateStateWithAudio(state, audio, {
    replaceAudio,
    addAudio: true,
  });
};

export const createLocalAudio = (state: SongListState, audio: Audio) => {
  const localAudioGuid = generateLocalAudioGuid(state.song?.audio);
  const retryableAudio = {
    ...audio,
    guid: localAudioGuid,
  };

  updateStateWithAudio(state, retryableAudio);
};

export const setAudioStateToUploading = (
  state: SongListState,
  audio: Audio
) => {
  const { parent, guid } = audio;
  const updatedAudio = {
    guid,
    parent,
    status: AudioStatusType.PROCESSING,
    statusReason: AudioStatusReasonType.UPLOADING,
  } as Audio;
  updateStateWithAudio(state, updatedAudio, { addAudio: false });
};

export const setAudioStateToTranscodingAndRemoveLocal = (
  state: SongListState,
  audio: Audio
) => {
  const { parent, guid } = audio;
  const updatedAudio = {
    guid,
    parent,
    status: AudioStatusType.PROCESSING,
    statusReason: AudioStatusReasonType.TRANSCODING,
  } as Audio;
  updateStateWithAudio(state, updatedAudio, { addAudio: false });
  state.localAudio[updatedAudio.parent] = state.localAudio[
    updatedAudio.parent
  ].filter((audio) => audio.guid !== updatedAudio.guid);
};

export const setAudioStateToRetryable = (
  state: SongListState,
  audio: Audio,
  file?: File
) => {
  const { parent, guid } = audio;
  const updatedAudio = {
    guid,
    parent,
    file: file || audio.file,
    statusReason: AudioStatusReasonType.RETRYABLE,
    status: AudioStatusType.FAILURE,
  } as Audio;
  updateStateWithAudio(state, updatedAudio, { addAudio: false });
};

export const updateAudioState = (
  state: SongListState,
  newAudio: Audio,
  oldAudio: Audio
) => {
  const updatedAudio = {
    ...newAudio,
    statusReason:
      oldAudio.statusReason === AudioStatusReasonType.UPLOADING
        ? oldAudio.statusReason
        : newAudio.statusReason,
  };
  updateStateWithAudio(state, updatedAudio, {
    addAudio: false,
  });
};

export const setAudioListWithoutLocalDuplicates = (
  state: SongListState,
  audio: Audio[]
) => {
  const localAudioList = getLocalAudioList(state);
  const songGuid = state.song?.guid;
  const filteredAudioList = localAudioList.filter(
    (localAudio) =>
      audio.findIndex((current) => current.guid === localAudio.guid) === -1
  );
  if (state.song) {
    state.song.audio = [...filteredAudioList, ...audio];
  }
  if (songGuid) {
    state.localAudio[songGuid] = filteredAudioList;
  }
};

export const filterAudioReadyForUpload = (
  state: SongListState,
  audio: Audio[]
) => {
  const completedAudioIds = audio
    .filter((audio) => audio.status === AudioStatusType.SUCCESS)
    .map((audio) => audio.guid);

  state.audioListReadyForUpload = state.audioListReadyForUpload.filter(
    (audioId) => !completedAudioIds.includes(audioId)
  );
};

export const updateLatestAudioVersion = (audio: Audio[], audioGuid: string) => {
  return audio.map((audio) =>
    audio.guid === audioGuid
      ? {
          ...audio,
          isLatestVersion: true,
          isPrivate: false,
        }
      : { ...audio, isLatestVersion: false }
  );
};

export const reorderFolders = ({
  items,
  folderGuid,
  targetFolderGuid,
}: {
  items: FolderItem[];
  folderGuid: string;
  targetFolderGuid: string;
}) => {
  const updated = [...items];

  const folderIndex = items.findIndex((item) => item.guid === folderGuid);
  const targetFolderIndex = items.findIndex(
    (item) => item.guid === targetFolderGuid
  );

  if (folderIndex === -1 || targetFolderIndex === -1) {
    return items;
  }

  const [movedElement] = updated.splice(folderIndex, 1);

  updated.splice(targetFolderIndex, 0, movedElement);

  return updated;
};
